import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";

export type ErrorBoundaryLevel = "root" | "page" | "inline";

export interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  onReset?: () => void;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: ErrorBoundaryLevel;
  title?: string;
  message?: string;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Sanitize error message to prevent accidental leakage of sensitive tokens or credentials in logs.
 */
function sanitizeErrorMessage(msg: string): string {
  return msg
    .replace(/Bearer\s+[A-Za-z0-9-_=.]+/gi, "Bearer [REDACTED]")
    .replace(/(password|secret|key|token)=["']?[^"'\s&]+["']?/gi, "$1=[REDACTED]");
}

/**
 * Enterprise React Error Boundary.
 * Catches render-phase errors in descendant trees, logs securely without leaking secrets,
 * and displays an accessible, recoverable fallback card.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const safeMsg = sanitizeErrorMessage(error?.message || "Unknown rendering error");
    console.error(`[ErrorBoundary] Caught error at ${this.props.level || "page"} level: ${safeMsg}`);

    if (this.props.onError) {
      try {
        this.props.onError(error, errorInfo);
      } catch (loggingErr) {
        console.error("[ErrorBoundary] Error inside custom onError handler:", loggingErr);
      }
    }
  }

  resetError = (): void => {
    if (this.props.onReset) {
      try {
        this.props.onReset();
      } catch (resetErr) {
        console.error("[ErrorBoundary] Error during onReset callback:", resetErr);
      }
    }
    this.setState({ hasError: false, error: null });
  };

  handleReload = (): void => {
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  };

  handleNavigateDashboard = (): void => {
    if (typeof window !== "undefined") {
      window.location.href = "/dashboard";
    }
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback, level = "page", title, message } = this.props;

    if (!hasError) {
      return children;
    }

    // Custom fallback provided by caller
    if (fallback) {
      if (typeof fallback === "function" && error) {
        return fallback(error, this.resetError);
      }
      return fallback as ReactNode;
    }

    const isRoot = level === "root";
    const isInline = level === "inline";

    const displayTitle = title || (isRoot ? "System Application Error" : "Something went wrong");
    const displayMessage =
      message ||
      (isRoot
        ? "An unexpected error interrupted the application shell. Please reload or return to the login screen."
        : "An unexpected error occurred while rendering this section. Your data is safe.");

    if (isInline) {
      return (
        <div
          data-testid="error-boundary-card"
          className="my-3 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
          role="alert"
        >
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0" aria-hidden="true" />
            <span>{displayMessage}</span>
          </div>
          <button
            type="button"
            data-testid="error-boundary-retry"
            onClick={this.resetError}
            className="inline-flex items-center rounded bg-amber-100 px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-amber-500"
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
            Try again
          </button>
        </div>
      );
    }

    return (
      <div
        data-testid="error-boundary-card"
        className={`flex ${isRoot ? "min-h-screen" : "min-h-[400px]"} w-full items-center justify-center p-4`}
        role="alert"
        aria-live="assertive"
      >
        <div className="w-full max-w-lg rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
          <div className="flex flex-col items-center text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-600">
              <AlertTriangle className="h-7 w-7" aria-hidden="true" />
            </div>

            <h2 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
              {displayTitle}
            </h2>

            <p className="mt-2 text-sm text-gray-600 leading-relaxed max-w-md">
              {displayMessage}
            </p>

            <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                data-testid="error-boundary-retry"
                onClick={this.resetError}
                className="inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </button>

              <button
                type="button"
                data-testid="error-boundary-reload"
                onClick={this.handleReload}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Reload Page
              </button>

              {!isRoot && (
                <button
                  type="button"
                  data-testid="error-boundary-home"
                  onClick={this.handleNavigateDashboard}
                  className="inline-flex items-center rounded-lg border border-transparent px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 focus:outline-none focus:underline"
                >
                  <Home className="mr-1.5 h-4 w-4" />
                  Dashboard
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
